import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LoginContainer,
  LoginContent,
  LogoWrapper,
  Subtitle,
  ErrorMessage,
  Spinner,
} from "../Login/index.styled";
import {
  consumeOAuthState,
  googleLoginCallback,
  GoogleLoginError,
  saveTokenFromLogin,
} from "../../utils/apiService";
import { store } from "../../store";
import { clearData } from "../../store/dashboardSlice";

const ERROR_MESSAGES: Record<string, string> = {
  user_not_found: "לא נמצא משתמש עם כתובת האימייל הזו.",
  code_exchange_failed: "שגיאה בתהליך ההתחברות. נסה שוב.",
  invalid_state: "שגיאת אימות. נסה שוב.",
  google_not_configured: "התחברות עם גוגל אינה מוגדרת.",
};

function getErrorMessage(err: unknown): string {
  if (err instanceof GoogleLoginError) {
    return (
      ERROR_MESSAGES[err.code] || err.message || "ההתחברות נכשלה. נסה שוב."
    );
  }
  return "ההתחברות עם גוגל נכשלה. נסה שוב.";
}

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invoking this effect.
    // consumeOAuthState() removes the state from sessionStorage on first call,
    // so a second invocation would find no state and incorrectly treat it as
    // a validation error, redirecting to /login despite a successful login.
    if (handledRef.current) return;
    handledRef.current = true;

    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const state = params.get("state");

      // Google may redirect with an error param (e.g. user denied consent)
      const googleError = params.get("error");
      if (googleError) {
        redirectWithError("ההתחברות עם גוגל בוטלה.");
        return;
      }

      if (!code || !state) {
        redirectWithError("פרמטרים חסרים מגוגל. נסה שוב.");
        return;
      }

      // Verify state matches what we stored before redirect
      const storedState = consumeOAuthState();
      if (!storedState || storedState !== state) {
        redirectWithError("שגיאת אימות. נסה שוב.");
        return;
      }

      try {
        const redirectUri = window.location.origin + "/auth/callback";
        const result = await googleLoginCallback(code, state, redirectUri);
        saveTokenFromLogin(result.token);
        store.dispatch(clearData());
        navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error("Google OAuth callback error:", err);
        redirectWithError(getErrorMessage(err));
      }
    };

    const redirectWithError = (message: string) => {
      setError(message);
      setTimeout(() => {
        navigate(`/login?error=${encodeURIComponent(message)}`, {
          replace: true,
        });
      }, 2000);
    };

    handleCallback();
  }, [location.search, navigate]);

  return (
    <LoginContainer>
      <LoginContent>
        <LogoWrapper>
          {error ? (
            <span style={{ color: "white", fontSize: 28 }}>!</span>
          ) : (
            <Spinner
              style={{ width: 32, height: 32, borderWidth: 3, color: "white" }}
            />
          )}
        </LogoWrapper>

        <Subtitle>{error || "מתחבר עם גוגל..."}</Subtitle>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </LoginContent>
    </LoginContainer>
  );
};

export default AuthCallback;
